import { useEffect, useState } from "react";
import {
  analyzeImage,
  assessmentDiscrepancyCheck,
  createAssessmentStepWithImage,
  finalizeAssessment,
  generateAssessmentRunId,
  getAssessmentData,
  getAssessmentRunId,
  getAssessments,
  getAssessmentSummary,
  resetAssessment,
  uploadFile,
} from "../../../dao/assessmement";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAlert } from "../../../actions/alert";

import errors from "./errors.json" with { type: "json" };
import toast from "react-hot-toast";

export function useAssessments() {
  const dispatch = useDispatch();
  const [assessments, setAssessments] = useState([]);

  useEffect(() => {
    const fetchAssessments = async () => {
      // TODO: Handle errors and loading states
      try {
        const assessmentsResponse = await getAssessments();
        setAssessments(assessmentsResponse.data);
      } catch (error) {
        console.error("Error fetching assessments:", error);
        dispatch(
          setAlert("Failed to load assessments. Please try again.", "danger"),
        );
      }
    };

    fetchAssessments();
  }, [dispatch]);

  return assessments;
}

function extractErrorMessage(response) {
  if (!response) return { message: "Unknown error" };

  // Case 1 & 2: Standard errors with arrays
  if (response.errors) {
    for (const [field, value] of Object.entries(response.errors)) {
      if (Array.isArray(value)) {
        // Example: { pose_type: ["This field is required."] }
        return {
          message: "Invalid input",
          description: `${field}: ${value.join(", ")}`,
        };
      } else if (typeof value === "object") {
        // Example: { image_url: { "Standing Side View": "HTTP Error 404: Not Found" } }
        const [key, msg] = Object.entries(value)[0];
        return {
          message: "Image loading error",
          description: `Can’t load image by ${field} (${key}): ${msg}`,
        };
      }
    }
  }

  // Case 3: Pose-specific error with message + code
  if (response?.aiResponse?.error && response.aiResponse.pose_type) {
    const { message, code } = response.aiResponse.error;

    for (const error of errors) {
      if (error.code === code.toString()) {
        return {
          message: error.message,
          description: error.description || "",
        };
      }
    }

    return {
      message: `Pose "${response.pose_type}" failed: ${message}`,
      description: `Error code ${code}`,
    };
  }

  // Fallback
  return { message: "An unexpected error occurred." };
}

async function getLatestAssessmentRunId() {
  const res = await getAssessmentRunId();
  return res?.status && res?.data?.length > 0 ? res.data[0].id : null;
}

export function useUpdateAssessment() {
  const navigate = useNavigate();
  const [fileUploadPercentage, setFileUploadPercentage] = useState(0);
  const [analysisError, setAnalysisError] = useState(null);

  const status = {
    PENDING: "Pending",
    IN_PROGRESS: "In Progress",
    COMPLETED: "Completed",
    FAILED: "Failed",
  };

  const [processingStatus, setProcessingStatus] = useState({
    FileUpload: status.PENDING,
    Analysis: status.PENDING,
    AssessmentCreation: status.PENDING,
  });

  function resetProcessingStatus() {
    setProcessingStatus({
      FileUpload: status.PENDING,
      Analysis: status.PENDING,
      AssessmentCreation: status.PENDING,
    });

    setFileUploadPercentage(0); // Reset file upload percentage
  }

  // TODO: Get rid of using userId/localStorage
  const userData = localStorage.getItem("user");
  const user = userData ? JSON.parse(userData) : null;

  // Function to update assessment
  // This function handles the file upload, analysis, and assessment creation.
  // It updates the processing status at each step and resets it after completion.
  async function updateAssessment(
    file,
    assessment,
    note,
    selectedOptions,
    previousUploadUrl,
  ) {
    setAnalysisError(null);
    setProcessingStatus({
      ...processingStatus,
      FileUpload: status.IN_PROGRESS,
    });

    // Upload file if it exists else take previous Upload Url
    let url = previousUploadUrl ?? null;
    let assessmentRunId;

    try {
      const runId = await getLatestAssessmentRunId(); // -> string | null
      // if runId exist use that else regenerate
      if (runId) {
        assessmentRunId = runId;
      } else {
        const generatedRes = await generateAssessmentRunId();
        if (generatedRes?.status) {
          assessmentRunId = generatedRes?.data?.id;
        }
      }
    } catch (error) {
      console.error("Error creating assessment run id:", error);
    }

    if (!assessmentRunId) {
      throw new Error("Failed to create assessment run. Please try again.");
    }

    if (file) {
      url = await uploadFile(
        file,
        file.type,
        assessmentRunId,
        assessment?.id,
        setFileUploadPercentage,
        file.name,
      );
    }

    setProcessingStatus({
      FileUpload: status.COMPLETED,
      Analysis: status.IN_PROGRESS,
      AssessmentCreation: status.PENDING,
    });

    const createAssessmentStepRequest = {
      assessmentId: assessment?.id,
      runId: assessmentRunId,
      note,
      assessmentFeedbackOptions:
        selectedOptions.length > 0
          ? selectedOptions.map((option) => ({
              id: option?.id,
              value: option?.option_value,
            }))
          : null,
      imageUrl: url,
    };

    try {
      const assessmentRes = await createAssessmentStepWithImage(
        createAssessmentStepRequest,
      );

      if (assessmentRes.errors) {
        console.error("Error creating assessment step:", assessmentRes.errors);
        throw new Error(assessmentRes.errors);
      }
    } catch (error) {
      const parsedError = extractErrorMessage(error?.response?.data || error);
      setAnalysisError(parsedError);
      const wrapped = new Error(
        parsedError.message || "Failed to analyze image",
      );
      wrapped.isUserAlertShown = false;
      throw wrapped;
    }

    setProcessingStatus({
      FileUpload: status.COMPLETED,
      Analysis: status.COMPLETED,
      AssessmentCreation: status.COMPLETED,
    });

    resetProcessingStatus();
  }

  // Function to finish the assessment
  // This function can be used to finalize the assessment process, e.g., by saving the
  // assessment summary or notifying the user.
  // It currently calls getAssessmentSummary to fetch the summary for the user.
  // You can modify it to perform additional actions as needed.
  // For example, you might want to reset the assessment state or redirect the user.
  async function finishAssessment() {
    try {
      const finalAssessmentResult = await finalizeAssessment();

      if (finalAssessmentResult?.status) {
        navigate("/finish-assessment");
      }
    } catch (error) {
      throw error;
    }
  }

  function clearAnalysisError() {
    setAnalysisError(null);
  }

  return {
    processingStatus,
    updateAssessment,
    finishAssessment,
    resetProcessingStatus,
    fileUploadPercentage,
    analysisError,
    clearAnalysisError,
  };
}

export function useAssessmentRunSummary() {
  const [assessmentRunSummary, setAssessmentRunSummary] = useState(null);

  const fetchAssessmentData = async () => {
    try {
      // TODO: Get rid of using userId/localStorage
      const userData = localStorage.getItem("user");
      const user = userData ? JSON.parse(userData) : null;

      const { data } = await getAssessmentSummary(user.id);

      if (!data || !Array.isArray(data)) {
        console.error("Invalid assessment summary data:", data);
        return;
      }

      const map = data.reduce((acc, item) => {
        acc[item.assessment_id] = item;
        return acc;
      }, {});

      setAssessmentRunSummary(map);
    } catch (err) {
      console.error("Error fetching assessment data:", err);
    }
  };

  useEffect(() => {
    fetchAssessmentData();
  }, []);

  return { assessmentRunSummary, fetchAssessmentData };
}

export function useResetAssessment() {
  const dispatch = useDispatch();
  const [resetting, setResetting] = useState(false);

  async function resetUserAssessment() {
    setResetting(true);
    try {
      await generateAssessmentRunId();
      dispatch(setAlert("Assessment has been reset successfully.", "success"));
      // window.location.reload(); // Reload the page to reflect changes
    } catch (error) {
      console.error("Error resetting assessment:", error);
      dispatch(
        setAlert("Failed to reset assessment. Please try again.", "danger"),
      );
    } finally {
      setResetting(false);
    }
  }

  return {
    resetting,
    resetUserAssessment,
  };
}

export function useFeedbackOptions(assessments, currentAssessmentIndex) {
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (assessments && assessments.length > 0) {
      const currentAssessmentFeedbackOptions =
        assessments[currentAssessmentIndex]?.assessmentFeedbackOption || [];
      const questions = currentAssessmentFeedbackOptions
        .filter((item) => item.is_question === 1)
        .map((item) => ({
          ...item,
          options: currentAssessmentFeedbackOptions.filter(
            (opt) => opt.parent_id === item?.id && opt.is_question === 0,
          ),
        }));

      setQuestions(questions);
    }
  }, [assessments, currentAssessmentIndex]);

  return questions;
}
